import LiveKitWaitingRoom from './LiveKitWaitingRoom';
import { handOffPreviewTracks } from './preview-tracks';
import usePreviewDevices from './usePreviewDevices';
import usePreviewMedia from './usePreviewMedia';

import beepAudio from '../../assets/sounds/beep.mp3';
import TelemedicineContext from '../../context/telemedicine';
import useDeviceList from '../../hooks/useDeviceList';
import { createAiConsent } from '../../services/telemedicine-ai-consent';
import { createFile, removeFile } from '../../services/telemedicine-file';
import { createConsentTerm, getConsentTerm } from '../../services/telemedicine-term';
import { clearPreferredAudioOutputId, getPreferredAudioOutputId, playAudioOutputTest, savePreferredAudioOutputId } from '../../utils/livekit-audio-output';
import { discardPreparedRoom, prepareRoom } from '../../utils/livekit-room';
import { ensureAudioDeviceLabels, isFrontFacingDevice, simplifyCameraDevices } from '../../utils/media-devices';
import { isAcceptedFile, MAX_FILE_SIZE_MB, normalizeUploadedFiles } from '../../utils/uploaded-files';

import { DateUtils, showErrorToast } from '@amigoapp/doca-react';
import { usePersistentUserChoices } from '@livekit/components-react';
import { supportsAudioOutputSelection } from 'livekit-client';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSound from 'use-sound';

const LiveKitWaitingRoomContainer = () => {
	const navigate = useNavigate();
	const [play] = useSound(beepAudio, { volume: 1 });
	const { telemedicine, updateTelemedicine } = useContext(TelemedicineContext);

	const {
		userChoices,
		saveAudioInputEnabled,
		saveVideoInputEnabled,
		saveAudioInputDeviceId,
		saveVideoInputDeviceId
	} = usePersistentUserChoices();

	const [isDoctorReady, setIsDoctorReady] = useState(false);
	const [audioEnabled, setAudioEnabled] = useState(userChoices.audioEnabled);
	const [videoEnabled, setVideoEnabled] = useState(userChoices.videoEnabled);
	const [showDeviceSettings, setShowDeviceSettings] = useState(false);
	const [activeSpeakerId, setActiveSpeakerId] = useState(getPreferredAudioOutputId());
	const [activeMicId, setActiveMicId] = useState(userChoices.audioDeviceId || '');
	const [deviceListKey, setDeviceListKey] = useState(0);

	const [consentTerm, setConsentTerm] = useState(null);
	const [showTerms, setShowTerms] = useState(false);
	const [showAttachments, setShowAttachments] = useState(false);
	const [showAiConsent, setShowAiConsent] = useState(false);
	const [isAnsweringAi, setIsAnsweringAi] = useState(false);
	const [files, setFiles] = useState([]);
	const [isSubmittingFile, setIsSubmittingFile] = useState(false);

	const showSpeakerSection = useMemo(() => supportsAudioOutputSelection(), []);

	const fileInputRef = useRef();
	const joiningRef = useRef(false);
	const signingRef = useRef(false);

	const { url, token } = telemedicine.livekit || {};

	const preparedRoom = useMemo(() => (url && token
		? prepareRoom({
			url,
			token,
			options: {
				videoDeviceId: userChoices.videoDeviceId,
				audioDeviceId: userChoices.audioDeviceId
			}
		})
		: null
	// eslint-disable-next-line react-hooks/exhaustive-deps
	), [url, token]);

	useEffect(() => () => {
		if (!joiningRef.current) {
			discardPreparedRoom();
		}
	}, []);

	const { videoTrack, handedOffRef, status: previewStatus } = usePreviewMedia({
		videoDeviceId: userChoices.videoDeviceId
	});

	const {
		devices: previewCameras,
		activeDeviceId: activeCameraId,
		isSwitching: isSwitchingCamera,
		selectDevice: selectCamera
	} = usePreviewDevices('videoinput', videoTrack, 'Não foi possível trocar a câmera.');

	const cameraDevices = simplifyCameraDevices(previewCameras);
	const isSelfViewMirrored = isFrontFacingDevice(previewCameras, activeCameraId);

	const micDevices = useDeviceList('audioinput', deviceListKey);
	const speakerDevices = useDeviceList('audiooutput', deviceListKey);

	useEffect(() => {
		if (!showDeviceSettings) {
			return undefined;
		}

		let cancelled = false;

		ensureAudioDeviceLabels().then(() => {
			if (!cancelled) {
				setDeviceListKey(current => current + 1);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [showDeviceSettings]);

	useEffect(() => {
		if (speakerDevices.length === 0) {
			return;
		}

		setActiveSpeakerId(current => {
			if (!current || speakerDevices.some(device => device.deviceId === current)) {
				return current;
			}

			clearPreferredAudioOutputId();

			return '';
		});
	}, [speakerDevices]);

	const onToggleAudio = useCallback(() => {
		setAudioEnabled(current => {
			saveAudioInputEnabled(!current);

			return !current;
		});
	}, [saveAudioInputEnabled]);

	const onToggleVideo = useCallback(() => {
		setVideoEnabled(current => {
			saveVideoInputEnabled(!current);

			return !current;
		});
	}, [saveVideoInputEnabled]);

	const onToggleDeviceSettings = useCallback(() => {
		setShowDeviceSettings(current => !current);
	}, []);

	const rememberInRoom = useCallback((kind, deviceId) => {
		preparedRoom?.switchActiveDevice(kind, deviceId).catch(() => {});
	}, [preparedRoom]);

	const onSelectCamera = useCallback(deviceId => {
		saveVideoInputDeviceId(deviceId);
		rememberInRoom('videoinput', deviceId);
		selectCamera(deviceId);
	}, [selectCamera, saveVideoInputDeviceId, rememberInRoom]);

	const onSelectMic = useCallback(deviceId => {
		setActiveMicId(deviceId);
		saveAudioInputDeviceId(deviceId);
		rememberInRoom('audioinput', deviceId);
	}, [saveAudioInputDeviceId, rememberInRoom]);

	const onSelectSpeaker = useCallback(deviceId => {
		setActiveSpeakerId(deviceId);
		savePreferredAudioOutputId(deviceId);
	}, []);

	const onTestSpeaker = useCallback(async () => {
		try {
			await playAudioOutputTest(beepAudio, activeSpeakerId);
		} catch (error) {
			clearPreferredAudioOutputId();
			setActiveSpeakerId('');
			showErrorToast('Não foi possível reproduzir na saída selecionada.');
		}
	}, [activeSpeakerId]);

	const onOpenTerms = useCallback(event => {
		event.preventDefault();
		setShowTerms(true);
	}, []);

	const onCloseTerms = useCallback(() => setShowTerms(false), []);

	const onOpenAttachments = useCallback(() => setShowAttachments(true), []);

	const onCloseAttachments = useCallback(() => setShowAttachments(false), []);

	const onSelectFiles = useCallback(async event => {
		const selectedFiles = Array.from(event.target.files || []);

		event.target.value = '';

		const acceptedFiles = selectedFiles.filter(isAcceptedFile);

		if (acceptedFiles.length < selectedFiles.length) {
			showErrorToast(`Envie apenas arquivos PDF, JPG ou PNG de até ${MAX_FILE_SIZE_MB} MB.`);
		}

		if (acceptedFiles.length === 0) {
			return;
		}

		setIsSubmittingFile(true);

		const { data } = await createFile(acceptedFiles);

		if (data) {
			setFiles(current => [...current, ...normalizeUploadedFiles(data, acceptedFiles)]);
		} else {
			showErrorToast('Não foi possível anexar o arquivo.');
		}

		setIsSubmittingFile(false);
	}, []);

	const onRemoveFile = useCallback(async id => {
		setFiles(current => current.filter(file => file.id !== id));

		try {
			await removeFile(id);
		} catch (error) { }
	}, []);

	const joinRoom = useCallback(() => {
		joiningRef.current = true;

		if (videoTrack && !videoEnabled) {
			videoTrack.stop();
		}

		handedOffRef.current = true;

		handOffPreviewTracks([videoEnabled ? videoTrack : null]);

		navigate('/video-room');
	}, [videoTrack, videoEnabled, handedOffRef, navigate]);

	const onJoinCall = useCallback(() => setShowAiConsent(true), []);

	const onCloseAiConsent = useCallback(() => setShowAiConsent(false), []);

	const answerAi = useCallback(async accepted => {
		if (isAnsweringAi) {
			return;
		}

		setIsAnsweringAi(true);

		const { error } = await createAiConsent(accepted);

		if (error) {
			showErrorToast('Não foi possível registrar sua escolha sobre o uso de IA.');
		}

		setIsAnsweringAi(false);
		joinRoom();
	}, [isAnsweringAi, joinRoom]);

	const onAcceptAi = useCallback(() => answerAi(true), [answerAi]);

	const onDeclineAi = useCallback(() => answerAi(false), [answerAi]);

	useEffect(() => {
		getConsentTerm().then(({ data }) => setConsentTerm(data));
	}, []);

	useEffect(() => {
		if (telemedicine.signed_at || signingRef.current) {
			return;
		}

		signingRef.current = true;

		createConsentTerm().then(({ data }) => {
			if (!data) {
				showErrorToast('Não foi possível criar o termo.');

				return;
			}

			window.socket.emit('telemedicine:patient-connected');
			updateTelemedicine({ signed_at: DateUtils.now() });
		});
	}, [telemedicine.signed_at, updateTelemedicine]);

	useEffect(() => {
		const handleRoomClosed = () => navigate('/telemedicine-finished');

		window.socket.on('telemedicine:room-closed', handleRoomClosed);

		return () => {
			window.socket.off('telemedicine:room-closed', handleRoomClosed);
		};
	}, [navigate]);

	useEffect(() => {
		const handleReady = async () => {
			await play();

			setIsDoctorReady(true);
		};

		window.socket.on('reconnect', () => {
			window.socket.emit('telemedicine:enter-room', {
				room_id: telemedicine?.id,
				patient_id: telemedicine?.patient_id
			});
		});

		window.socket.on('telemedicine:start-video-call', room => {
			updateTelemedicine({
				room_sid: room.room_sid,
				room_token: room.token
			});

			handleReady();
		});

		window.socket.on('telemedicine:connected', handleReady);

		window.socket.emit('telemedicine:enter-room', {
			room_id: telemedicine.id,
			patient_id: telemedicine.patient_id
		});

		return () => {
			window.socket.off('telemedicine:connected', handleReady);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<LiveKitWaitingRoom
			activeCameraId={activeCameraId}
			activeMicId={activeMicId}
			activeSpeakerId={activeSpeakerId}
			attendance={telemedicine.attendance}
			audioEnabled={audioEnabled}
			cameraDevices={cameraDevices}
			company={telemedicine.company}
			consentTerm={consentTerm}
			createdAt={telemedicine.created_at}
			fileInputRef={fileInputRef}
			files={files}
			isAnsweringAi={isAnsweringAi}
			isDoctorReady={isDoctorReady}
			isSelfViewMirrored={isSelfViewMirrored}
			isSubmittingFile={isSubmittingFile}
			isSwitchingCamera={isSwitchingCamera}
			micDevices={micDevices}
			previewStatus={previewStatus}
			showAiConsent={showAiConsent}
			showAttachments={showAttachments}
			showDeviceSettings={showDeviceSettings}
			showSpeakerSection={showSpeakerSection}
			showTerms={showTerms}
			speakerDevices={speakerDevices}
			user={telemedicine.user}
			videoEnabled={videoEnabled}
			videoTrack={videoTrack}
			onAcceptAi={onAcceptAi}
			onCloseAiConsent={onCloseAiConsent}
			onCloseAttachments={onCloseAttachments}
			onCloseTerms={onCloseTerms}
			onDeclineAi={onDeclineAi}
			onJoinCall={onJoinCall}
			onOpenAttachments={onOpenAttachments}
			onOpenTerms={onOpenTerms}
			onRemoveFile={onRemoveFile}
			onSelectCamera={onSelectCamera}
			onSelectFiles={onSelectFiles}
			onSelectMic={onSelectMic}
			onSelectSpeaker={onSelectSpeaker}
			onTestSpeaker={onTestSpeaker}
			onToggleAudio={onToggleAudio}
			onToggleDeviceSettings={onToggleDeviceSettings}
			onToggleVideo={onToggleVideo}
		/>
	);
};

export default LiveKitWaitingRoomContainer;
